import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { ProductSuiteButton } from '@parrot/product-suite';
import { UserMenu } from '../UserMenu/UserMenu';
import { SpotlightSearch } from '../SpotlightSearch/SpotlightSearch';
import { PlexieDrawer } from '../plexie-drawer/plexie-drawer';
import { ProductSuiteDrawer } from '../product-suite-drawer/product-suite-drawer';
import styles from './Header.module.css';

export function Header() {
  const navigate = useNavigate();
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const [plexieOpen, setPlexieOpen] = useState(false);
  const [productSuiteOpen, setProductSuiteOpen] = useState(false);

  // ⌘K shortcut for search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSpotlightOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <>
      <header className={`app-chrome ${styles.topnav}`}>
        {/* Left: Back/Forward nav */}
        <div className={styles.left}>
          <button
            className={styles.navBtn}
            onClick={() => navigate(-1)}
            title="Back"
            aria-label="Back"
          >
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
              <path
                d="M6 1L1 6l5 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            className={styles.navBtn}
            onClick={() => navigate(1)}
            title="Forward"
            aria-label="Forward"
          >
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
              <path
                d="M1 1l5 5-5 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Center: Search trigger */}
        <div className={styles.center}>
          <button
            className={styles.searchTrigger}
            onClick={() => setSpotlightOpen(true)}
            aria-label="Search"
          >
            <Search size={15} strokeWidth={2} className={styles.searchIcon} />
            <span className={styles.searchText}>Quick Search Anything</span>
            <kbd className={styles.kbd}>⌘K</kbd>
          </button>
        </div>

        {/* Right: Product Suite, Plexie, Avatar */}
        <div className={styles.right}>
          <ProductSuiteButton
            onClick={() => setProductSuiteOpen((o) => !o)}
            theme="dark"
          />
          <button
            className={`${styles.btn} ${styles.btnAi}`}
            title="Plexie AI"
            aria-label="Plexie Assistant"
            onClick={() => setPlexieOpen((o) => !o)}
          >
            <img
              src="https://cdn-fastly.parrotanalytics.com/general-assets/plexie-icon-v3.png"
              className={`${styles.aiOrb} ${styles.aiOrbDefault}`}
              width="38"
              height="38"
              alt=""
            />
            <img
              src="https://cdn-fastly.parrotanalytics.com/general-assets/plexie-icon-hover-v3.png"
              className={`${styles.aiOrb} ${styles.aiOrbHover}`}
              width="38"
              height="38"
              alt=""
            />
          </button>
          <UserMenu />
        </div>
      </header>

      <SpotlightSearch open={spotlightOpen} onClose={() => setSpotlightOpen(false)} />
      <PlexieDrawer open={plexieOpen} onClose={() => setPlexieOpen(false)} />
      <ProductSuiteDrawer open={productSuiteOpen} onClose={() => setProductSuiteOpen(false)} />
    </>
  );
}
